/**
 * Appointments section component for displaying and managing appointments
 */

import React from 'react';
import { CheckCircle, Event, Schedule, Close, Edit } from '@mui/icons-material';
import { Ticket } from '../../types/ticketTypes';
import { formatAppointmentDateTime, formatTimestamp } from '../utils/formatUtils';
import { AppointmentsHandlers } from '../hooks/useAppointments';

export interface AppointmentsSectionProps {
  ticket: Ticket;
  tickets: Ticket[];
  appointmentsHandlers: AppointmentsHandlers;
}

export const AppointmentsSection: React.FC<AppointmentsSectionProps> = ({
  ticket,
  appointmentsHandlers
}) => {
  const {
    handleToggleAppointmentStatus,
    handleCancelAppointment,
    handleEditAppointment
  } = appointmentsHandlers;

  // Only render if there are appointments
  if (!ticket.appointments || ticket.appointments.length === 0) {
    return null;
  }

  return (
    <>
      <div className="ticket-details-section">
        <div className="section-header">
          <h4>Appointments</h4>
        </div>
        
        <div className="appointments-container">
          {ticket.appointments
            .sort((a, b) => new Date(`${a.date}T${a.time}`).getTime() - new Date(`${b.date}T${b.time}`).getTime())
            .map(appointment => (
            <div key={appointment.id} className={`appointment ${appointment.status}`}>
              <div className="appointment-header">
                <div className="appointment-meta">
                  <span className="appointment-datetime">
                    <Event style={{ fontSize: '16px', marginRight: '4px' }} />
                    {formatAppointmentDateTime(appointment.date, appointment.time)}
                  </span>
                  <span className="appointment-owner">
                    Assigned to: {appointment.owner}
                  </span>
                </div>
                <div className="appointment-actions">
                  {appointment.status !== 'cancelled' && (
                    <>
                      <button
                        type="button"
                        className="appointment-edit-btn"
                        onClick={() => handleEditAppointment(ticket, appointment.id)}
                        title="Edit appointment"
                      >
                        <Edit />
                      </button>
                      <button
                        type="button"
                        className={`appointment-toggle-btn ${appointment.status}`}
                        onClick={() => handleToggleAppointmentStatus(ticket, appointment.id)}
                        title={appointment.status === 'scheduled' ? 'Mark as completed' : 'Mark as scheduled'}
                      >
                        {appointment.status === 'completed' ? <CheckCircle /> : <Schedule />}
                      </button>
                    </>
                  )}
                  {appointment.status === 'scheduled' && (
                    <button
                      type="button"
                      className="appointment-cancel-btn"
                      onClick={() => handleCancelAppointment(ticket, appointment.id)}
                      title="Cancel appointment"
                    >
                      <Close />
                    </button>
                  )}
                </div>
              </div>
              <div className="appointment-content">
                <strong>Task:</strong> {appointment.task}
              </div>
              <div className="appointment-footer">
                <span className="appointment-created">
                  Created by {appointment.createdBy} on {formatTimestamp(appointment.createdAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};